import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDate, fmtNum, groupByBasket } from "@/lib/helpers";
import { canAct } from "@/lib/access";
import { Trash2, Filter, FileText, Eye, ChevronRight, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import SaleDetailDialog from "@/components/SaleDetailDialog";
import { RequestSentDialog, DeleteRequestsPanel, decideRequest, errText } from "@/components/DeleteRequests";

function StatusDot({ st }) {
  if (!st) return null;
  const cls = st === "tamam" ? "bg-emerald-500" : st === "sorunlu" ? "bg-red-500" : st === "beklemede" ? "bg-amber-500" : "bg-slate-500";
  return <span title={`Kimlik: ${st}`} className={`inline-block h-2 w-2 rounded-full ${cls}`} />;
}

function PendingTag({ dr, me, testKey }) {
  if (!dr) return null;
  const mine = dr.by_id === me?.id;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid={`sale-pending-${testKey}`}
          title={`${dr.by_name} silme talep etti`}>
      Silme bekliyor
      {(mine || me?.role === "koordinator") && (
        <button onClick={(e) => { e.stopPropagation(); decideRequest(dr.id, "cancel"); }} className="underline hover:text-amber-100" data-testid={`sale-pending-cancel-${testKey}`}>Geri çek</button>
      )}
    </span>
  );
}

function RowActions({ onView, onDelete, canDelete, testKey, pending, me }) {
  return (
    <TableCell onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-end gap-1">
        <PendingTag dr={pending} me={me} testKey={testKey} />
        {onView && (
          <Button size="sm" variant="ghost" onClick={onView} data-testid={`view-sale-${testKey}`}>
            <Eye size={14} className="text-blue-300" />
          </Button>
        )}
        {canDelete && !pending && (
          <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`delete-sale-${testKey}`}>
            <Trash2 size={14} className="text-red-400" />
          </Button>
        )}
      </div>
    </TableCell>
  );
}

function SaleRow({ s, canDelete, showUser, selected, toggleSelect, onOpen, onDelete, child, me }) {
  return (
    <TableRow className={`border-slate-800 cursor-pointer hover:bg-slate-800/40 ${child ? "bg-slate-950/40" : ""}`}
              onClick={() => onOpen(s.id)} data-testid={`sale-row-${s.id}`}>
      {canDelete && (
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleSelect(s.id)} data-testid={`sale-select-${s.id}`} />
        </TableCell>
      )}
      <TableCell className="whitespace-nowrap text-xs font-mono text-slate-400">{child ? "" : fmtDate(s.created_at)}</TableCell>
      <TableCell className={child ? "pl-8" : ""}>
        <div className="font-semibold text-sm flex items-center gap-1.5">
          {s.product_name}
          {!child && <StatusDot st={s.id_card_status} />}
          {s.edited_at && <span title="Düzenlendi" className="text-[9px] font-bold text-blue-300 px-1 rounded bg-blue-500/20">D</span>}
        </div>
        <div className="text-[10px] font-mono text-slate-500">{s.product_sku}</div>
      </TableCell>
      <TableCell className="text-sm">{child ? "" : s.customer_name || "-"}</TableCell>
      {showUser && <TableCell className="text-sm">{child ? "" : s.user_name}</TableCell>}
      <TableCell className="text-right font-semibold">{fmtNum(s.quantity)}</TableCell>
      <TableCell className="text-right font-bold text-amber-300">{fmtNum(s.points)}</TableCell>
      <TableCell className="text-xs text-slate-400 max-w-[200px] truncate">{child ? "" : s.customer_note || "-"}</TableCell>
      <RowActions onView={() => onOpen(s.id)} onDelete={() => onDelete(s.id)} canDelete={canDelete} testKey={s.id} pending={s.delete_request} me={me} />
    </TableRow>
  );
}

function BasketRows({ g, canDelete, showUser, open, onToggle, selected, setSelected, toggleSelect, onOpen, onDelete, onDeleteAll, me }) {
  const s = g.first;
  const allPending = g.items.every((x) => x.delete_request);
  const freeIds = g.items.filter((x) => !x.delete_request).map((x) => x.id);
  const ids = g.items.map((x) => x.id);
  const allSel = ids.every((i) => selected.has(i));
  const toggleAll = () => {
    const n = new Set(selected);
    ids.forEach((i) => (allSel ? n.delete(i) : n.add(i)));
    setSelected(n);
  };
  return (
    <>
      <TableRow className="border-slate-800 cursor-pointer hover:bg-slate-800/40" onClick={onToggle} data-testid={`basket-row-${g.key}`}>
        {canDelete && (
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid={`basket-select-${g.key}`} />
          </TableCell>
        )}
        <TableCell className="whitespace-nowrap text-xs font-mono text-slate-400">{fmtDate(s.created_at)}</TableCell>
        <TableCell>
          <div className="font-semibold text-sm flex items-center gap-1.5">
            <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
            <ShoppingCart size={14} className="text-blue-300" /> Sepet · {g.items.length} ürün
            <StatusDot st={s.id_card_status} />
          </div>
          <div className="text-[10px] font-mono text-slate-500 pl-5">{g.code}</div>
        </TableCell>
        <TableCell className="text-sm">{s.customer_name || "-"}</TableCell>
        {showUser && <TableCell className="text-sm">{s.user_name}</TableCell>}
        <TableCell className="text-right font-semibold">{fmtNum(g.qty)}</TableCell>
        <TableCell className="text-right font-bold text-amber-300">{fmtNum(g.points)}</TableCell>
        <TableCell className="text-xs text-slate-400 max-w-[200px] truncate">{s.customer_note || "-"}</TableCell>
        <RowActions onDelete={() => onDeleteAll(freeIds)} canDelete={canDelete} testKey={`basket-${g.key}`} pending={allPending ? s.delete_request : null} me={me} />
      </TableRow>
      {open && g.items.map((x) => (
        <SaleRow key={x.id} s={x} child canDelete={canDelete} showUser={showUser} selected={selected}
                 toggleSelect={toggleSelect} onOpen={onOpen} onDelete={onDelete} me={me} />
      ))}
    </>
  );
}

export default function Sales() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [saleSelected, setSaleSelected] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [sent, setSent] = useState(0);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const canDelete = canAct(user, "delete_sales");
  const showFilters = user?.role !== "personel";

  const load = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate + "T23:59:59");
    if (branchFilter && branchFilter !== "all") params.append("branch_id", branchFilter);
    if (userFilter && userFilter !== "all") params.append("user_id", userFilter);
    http.get(`/sales?${params.toString()}`).then((r) => { setSales(r.data); setSelected(new Set()); });
  };

  useEffect(() => {
    if (showFilters) {
      http.get("/branches").then((r) => setBranches(r.data));
      http.get("/users").then((r) => setUsers(r.data));
    }
  }, [showFilters]);

  const tick = useRealtimeTick(() => selectedRef.current.size === 0);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate, branchFilter, userFilter, tick]);

  const totalQty = sales.reduce((s, x) => s + (x.quantity || 0), 0);
  const totalPoints = sales.reduce((s, x) => s + (x.points || 0), 0);

  const afterDelete = (data) => {
    if (data.pending) setSent(data.requested || 1);
    else toast.success(`${data.deleted} satış silindi`);
    setSelected(new Set());
    load();
  };

  const remove = async (id) => {
    try { afterDelete((await http.delete(`/sales/${id}`)).data); }
    catch (e) { toast.error(errText(e, "Silinemedi")); }
  };

  const toggleExpand = (k) => {
    const e = new Set(expanded);
    if (e.has(k)) e.delete(k); else e.add(k);
    setExpanded(e);
  };

  const bulkDelete = () => deleteIds(Array.from(selected));

  const deleteIds = async (ids) => {
    if (ids.length === 0) return;
    try { afterDelete((await http.post("/sales/bulk-delete", { ids })).data); }
    catch (e) { toast.error(errText(e, "Silinemedi")); setSelected(new Set()); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === sales.length) setSelected(new Set());
    else setSelected(new Set(sales.map((s) => s.id)));
  };

  const branchUsers = branchFilter && branchFilter !== "all" ? users.filter((u) => u.branch_id === branchFilter) : users;

  return (
    <div className="space-y-6">
      <header className="animate-fade-up">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold">Satışlar</h1>
            <p className="text-slate-400 text-sm mt-1">Günlük ve geçmiş satışlar.</p>
          </div>
          {canDelete && selected.size > 0 && (
            <Button variant="destructive" onClick={bulkDelete} className="bg-red-600 hover:bg-red-500" data-testid="sales-bulk-delete">
              <Trash2 size={14} className="mr-1" /> {selected.size} {user?.role === "koordinator" ? "Sil" : "Silme Talebi"}
            </Button>
          )}
        </div>
      </header>

      {user?.role === "koordinator" && <DeleteRequestsPanel />}

      {showFilters && (
        <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Filter size={14} /> Filtreler</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-4">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-slate-950/50 border-slate-700" data-testid="filter-start-date" />
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-slate-950/50 border-slate-700" data-testid="filter-end-date" />
            {user?.role !== "mudur" && (
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="bg-slate-950/50 border-slate-700" data-testid="filter-branch"><SelectValue placeholder="Tüm Şubeler" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Şubeler</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="bg-slate-950/50 border-slate-700" data-testid="filter-user"><SelectValue placeholder="Tüm Personel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Personel</SelectItem>
                {branchUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-3 animate-fade-up delay-200">
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 glass p-4">
          <p className="text-[10px] uppercase font-mono text-slate-500">İşlem</p>
          <p className="font-display text-2xl font-bold">{fmtNum(groupByBasket(sales).length)}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 glass p-4">
          <p className="text-[10px] uppercase font-mono text-slate-500">Adet</p>
          <p className="font-display text-2xl font-bold text-emerald-300">{fmtNum(totalQty)}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 glass p-4">
          <p className="text-[10px] uppercase font-mono text-slate-500">Puan</p>
          <p className="font-display text-2xl font-bold text-amber-300">{fmtNum(totalPoints)}</p>
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                {canDelete && (
                  <TableHead className="w-10">
                    <Checkbox checked={sales.length > 0 && selected.size === sales.length}
                              onCheckedChange={toggleAll} data-testid="sales-select-all" />
                  </TableHead>
                )}
                <TableHead className="text-slate-400">Tarih</TableHead>
                <TableHead className="text-slate-400">Ürün</TableHead>
                <TableHead className="text-slate-400">Müşteri</TableHead>
                {user?.role !== "personel" && <TableHead className="text-slate-400">Personel</TableHead>}
                <TableHead className="text-slate-400 text-right">Adet</TableHead>
                <TableHead className="text-slate-400 text-right">Puan</TableHead>
                <TableHead className="text-slate-400">Not</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupByBasket(sales).map((g) => g.items.length === 1 && !g.code ? (
                <SaleRow key={g.key} s={g.first} canDelete={canDelete} showUser={user?.role !== "personel"}
                         selected={selected} toggleSelect={toggleSelect} onOpen={setSaleSelected} onDelete={remove} me={user} />
              ) : (
                <BasketRows key={g.key} g={g} canDelete={canDelete} showUser={user?.role !== "personel"}
                            open={expanded.has(g.key)} onToggle={() => toggleExpand(g.key)}
                            selected={selected} setSelected={setSelected} toggleSelect={toggleSelect}
                            onOpen={setSaleSelected} onDelete={remove} onDeleteAll={deleteIds} me={user} />
              ))}
              {sales.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canDelete ? 8 : 7} className="text-center text-slate-500 py-10">
                    <FileText className="mx-auto mb-2" size={24} />
                    Henüz satış kaydı yok
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <RequestSentDialog open={sent > 0} count={sent} onClose={() => setSent(0)} />
      <SaleDetailDialog saleId={saleSelected} onClose={() => setSaleSelected(null)} onUpdated={load} />
    </div>
  );
}
